import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Plus, Edit2, Trash2, Eye, Star, Search, Filter,
  FileText, BarChart3, Loader2, Image as ImageIcon, 
  ExternalLink, Save, X, ChevronLeft, FolderOpen, Settings2, Pencil
} from 'lucide-react';
import { toast } from 'sonner';
import TipTapEditor from '../../components/ui/TipTapEditor';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import LanguageTabs, { useAvailableLanguages, getContentWithFallback } from '../../components/admin/LanguageTabs';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminBlog = () => {
  const { token } = useAdminAuth();
  const { languages, loading: languagesLoading, defaultLanguage } = useAvailableLanguages();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Main tabs
  const [mainTab, setMainTab] = useState('posts'); // 'posts' | 'categories'
  
  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  
  // Editor state
  const [editingPost, setEditingPost] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  
  // Category editor state
  const [editingCategory, setEditingCategory] = useState(null);
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    title: {},
    slug: '',
    excerpt: {},
    content: {},
    categoryId: '',
    featuredImage: '',
    seoTitle: {},
    seoDescription: {},
    status: 'draft',
    isFeatured: false
  });
  const [activeTab, setActiveTab] = useState('pl');

  useEffect(() => {
    if (defaultLanguage) {
      setActiveTab(defaultLanguage);
    }
  }, [defaultLanguage]);

  useEffect(() => {
    fetchData();
  }, [page, statusFilter, categoryFilter]);

  const fetchData = async () => {
    try {
      const [postsRes, catsRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/admin/blog/posts`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            page,
            limit: 20,
            status: statusFilter !== 'all' ? statusFilter : undefined,
            categoryId: categoryFilter !== 'all' ? categoryFilter : undefined,
            search: search || undefined
          }
        }),
        axios.get(`${API}/api/admin/blog/categories`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/admin/blog/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setPosts(postsRes.data.posts);
      setPagination(postsRes.data.pagination);
      setCategories(catsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status !== 404) {
        toast.error('Błąd podczas pobierania danych');
      }
    } finally {
      setLoading(false);
    }
  };

  const initializeCategories = async () => {
    try {
      await axios.post(`${API}/api/admin/blog/categories/initialize`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Kategorie zainicjalizowane');
      fetchData();
    } catch (error) {
      if (error.response?.data?.error?.includes('already exist')) {
        toast.info('Kategorie już istnieją');
      } else {
        toast.error('Błąd podczas inicjalizacji');
      }
    }
  };

  // Upload image to server
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file
    if (!file.type.startsWith('image/')) {
      toast.error('Proszę wybrać plik obrazu');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Plik jest za duży (max 5MB)');
      return;
    }
    
    setUploadingImage(true);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);
      
      const response = await axios.post(`${API}/api/admin/blog/upload-image`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.url) {
        setFormData(prev => ({ ...prev, featuredImage: response.data.url }));
        toast.success('Zdjęcie zostało przesłane');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Błąd podczas przesyłania zdjęcia');
    } finally {
      setUploadingImage(false);
      e.target.value = ''; // Reset input
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchData();
  };

  const openEditor = (post = null) => {
    if (post) {
      setEditingPost(post);
      setFormData({
        title: post.title || { pl: '', en: '' },
        slug: post.slug || '',
        excerpt: post.excerpt || { pl: '', en: '' },
        content: post.content || { pl: '', en: '' },
        categoryId: post.categoryId || '',
        featuredImage: post.featuredImage || '',
        seoTitle: post.seoTitle || { pl: '', en: '' },
        seoDescription: post.seoDescription || { pl: '', en: '' },
        status: post.status || 'draft',
        isFeatured: post.isFeatured || false
      });
    } else {
      setEditingPost(null);
      setFormData({
        title: { pl: '', en: '' },
        slug: '',
        excerpt: { pl: '', en: '' },
        content: { pl: '', en: '' },
        categoryId: '',
        featuredImage: '',
        seoTitle: { pl: '', en: '' },
        seoDescription: { pl: '', en: '' },
        status: 'draft',
        isFeatured: false
      });
    }
    setActiveTab('pl');
    setShowEditor(true);
  };

  const closeEditor = () => {
    setShowEditor(false);
    setEditingPost(null);
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[ąćęłńóśźż]/g, c => ({
        'ą': 'a', 'ć': 'c', 'ę': 'e', 'ł': 'l', 'ń': 'n',
        'ó': 'o', 'ś': 's', 'ź': 'z', 'ż': 'z'
      }[c] || c))
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleTitleChange = (lang, value) => {
    setFormData(prev => ({
      ...prev,
      title: { ...prev.title, [lang]: value },
      slug: lang === 'pl' && !editingPost ? generateSlug(value) : prev.slug
    }));
  };

  const handleSave = async () => {
    if (!formData.title.pl || !formData.slug) {
      toast.error('Tytuł (PL) i slug są wymagane');
      return;
    }

    try {
      setSaving(true);
      
      if (editingPost) {
        await axios.put(`${API}/api/admin/blog/posts/${editingPost.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Post zaktualizowany');
      } else {
        await axios.post(`${API}/api/admin/blog/posts`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Post utworzony');
      }
      
      closeEditor();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Błąd podczas zapisywania');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten post?')) return;
    
    try {
      await axios.delete(`${API}/api/admin/blog/posts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post usunięty');
      fetchData();
    } catch (error) {
      toast.error('Błąd podczas usuwania');
    }
  };

  const toggleFeatured = async (id, currentState) => {
    try {
      await axios.patch(`${API}/api/admin/blog/posts/${id}/featured`, 
        { isFeatured: !currentState },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchData();
    } catch (error) {
      toast.error('Błąd podczas zmiany statusu');
    }
  };

  // Category management functions
  const emptyCategoryForm = {
    name: {},
    slug: '',
    description: {},
    order: 0
  };

  const handleSaveCategory = async () => {
    try {
      if (!editingCategory.slug || !editingCategory.name?.pl) {
        toast.error('Slug i nazwa (PL) są wymagane');
        return;
      }
      
      setSaving(true);
      
      if (isCreatingCategory) {
        await axios.post(`${API}/api/admin/blog/categories`, editingCategory, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Kategoria utworzona');
      } else {
        await axios.put(`${API}/api/admin/blog/categories/${editingCategory.id}`, editingCategory, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Kategoria zaktualizowana');
      }
      
      setEditingCategory(null);
      setIsCreatingCategory(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Błąd podczas zapisywania kategorii');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć tę kategorię?')) return;
    
    try {
      await axios.delete(`${API}/api/admin/blog/categories/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Kategoria usunięta');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Błąd podczas usuwania');
    }
  };

  if (loading || languagesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Editor view
  if (showEditor) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={closeEditor}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Powrót do listy
          </Button>
          <div className="flex items-center gap-2">
            <Select
              value={formData.status}
              onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
            >
              <SelectTrigger className="w-32 bg-white/5 border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Szkic</SelectItem>
                <SelectItem value="published">Opublikowany</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleSave} disabled={saving} className="bg-primary">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Zapisz
            </Button>
          </div>
        </div>

        <Card className="bg-white/5 border-white/10">
          <CardContent className="pt-6 space-y-6">
            {/* Language tabs - dynamic from API */}
            <div className="border-b border-white/10 pb-4">
              <Label className="text-gray-400 text-sm mb-2 block">Język treści</Label>
              <LanguageTabs
                activeTab={activeTab}
                onTabChange={setActiveTab}
                showIndicator={true}
                hasContent={{
                  ...Object.fromEntries(languages.map(l => [l.code, !!formData.title?.[l.code]]))
                }}
              />
            </div>

            {/* Title & Slug */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-400">Tytuł ({activeTab.toUpperCase()}) *</Label>
                <Input
                  value={formData.title[activeTab] || ''}
                  onChange={(e) => handleTitleChange(activeTab, e.target.value)}
                  placeholder="Tytuł artykułu"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-gray-400">Slug (URL) *</Label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData(prev => ({ ...prev, slug: generateSlug(e.target.value) }))}
                  placeholder="tytul-artykulu"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
            </div>

            {/* Category & Featured Image */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-400">Kategoria</Label>
                <Select
                  value={formData.categoryId || 'none'}
                  onValueChange={(value) => setFormData(prev => ({ 
                    ...prev, 
                    categoryId: value === 'none' ? '' : value 
                  }))}
                >
                  <SelectTrigger className="bg-white/5 border-white/10">
                    <SelectValue placeholder="Wybierz kategorię" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Brak kategorii</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name?.pl || cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-400 mb-2 block">Zdjęcie główne</Label>
                <div className="space-y-3">
                  {/* Image preview */}
                  {formData.featuredImage && (
                    <div className="relative aspect-video rounded-lg overflow-hidden bg-white/5">
                      <img 
                        src={formData.featuredImage} 
                        alt="Featured" 
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, featuredImage: '' }))}
                        className="absolute top-2 right-2 p-1 bg-red-500/80 rounded-full hover:bg-red-500"
                      >
                        <X className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  )}
                  
                  {/* Upload options */}
                  <div className="flex gap-3">
                    {/* File upload button */}
                    <label className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-purple-600/20 border-2 border-dashed border-purple-500/50 rounded-lg cursor-pointer hover:bg-purple-600/30 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={uploadingImage}
                      />
                      {uploadingImage ? (
                        <>
                          <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
                          <span className="text-purple-400">Przesyłanie...</span>
                        </>
                      ) : (
                        <>
                          <ImageIcon className="w-5 h-5 text-purple-400" />
                          <span className="text-purple-400">Wybierz z komputera</span>
                        </>
                      )}
                    </label>
                  </div>
                  
                  {/* URL input */}
                  <div className="flex gap-2">
                    <Input
                      value={formData.featuredImage}
                      onChange={(e) => setFormData(prev => ({ ...prev, featuredImage: e.target.value }))}
                      placeholder="lub wklej URL zdjęcia..."
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Featured toggle */}
            <div className="flex items-center gap-3">
              <Switch
                checked={formData.isFeatured}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, isFeatured: checked }))}
              />
              <Label className="text-gray-400">Polecany (wyświetlany na górze listy)</Label>
            </div>

            {/* Excerpt */}
            <div>
              <Label className="text-gray-400">Krótki opis ({activeTab.toUpperCase()}) - 2-3 linijki</Label>
              <textarea
                value={formData.excerpt[activeTab] || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  excerpt: { ...prev.excerpt, [activeTab]: e.target.value }
                }))}
                placeholder="Krótki opis widoczny na liście artykułów..."
                rows={3}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white resize-none"
              />
            </div>

            {/* Content */}
            <div>
              <Label className="text-gray-400 mb-2 block">Treść ({activeTab.toUpperCase()})</Label>
              <TipTapEditor
                content={formData.content[activeTab] || ''}
                onChange={(html) => setFormData(prev => ({
                  ...prev,
                  content: { ...prev.content, [activeTab]: html }
                }))}
                placeholder="Zacznij pisać treść artykułu..."
                uploadEndpoint="/api/admin/blog/upload-image"
              />
            </div>

            {/* SEO */}
            <div className="border-t border-white/10 pt-6">
              <h4 className="text-white font-medium mb-4">SEO ({activeTab.toUpperCase()})</h4>
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-400">Meta Title</Label>
                  <Input
                    value={formData.seoTitle[activeTab] || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      seoTitle: { ...prev.seoTitle, [activeTab]: e.target.value }
                    }))}
                    placeholder="Tytuł dla wyszukiwarek (max 60 znaków)"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-400">Meta Description</Label>
                  <textarea
                    value={formData.seoDescription[activeTab] || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      seoDescription: { ...prev.seoDescription, [activeTab]: e.target.value }
                    }))}
                    placeholder="Opis dla wyszukiwarek (max 160 znaków)"
                    rows={2}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white resize-none"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Blog</h1>
          <p className="text-gray-400">Zarządzaj artykułami i kategoriami</p>
        </div>
      </div>

      <Tabs value={mainTab} onValueChange={setMainTab} className="w-full">
        <TabsList className="bg-white/5 border-white/10">
          <TabsTrigger value="posts" className="data-[state=active]:bg-primary">
            <FileText className="w-4 h-4 mr-2" />
            Artykuły
          </TabsTrigger>
          <TabsTrigger value="categories" className="data-[state=active]:bg-primary">
            <FolderOpen className="w-4 h-4 mr-2" />
            Kategorie
          </TabsTrigger>
        </TabsList>

        {/* Posts Tab */}
        <TabsContent value="posts" className="mt-6 space-y-6">
          <div className="flex justify-end gap-2">
            {categories.length === 0 && (
              <Button onClick={initializeCategories} variant="outline" className="border-white/20">
                Zainicjalizuj kategorie
              </Button>
            )}
            <Button onClick={() => openEditor()} className="bg-primary">
              <Plus className="w-4 h-4 mr-2" />
              Nowy artykuł
            </Button>
          </div>

          {/* Stats */}
          {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-white/5 border-white/10">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8 text-primary" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats.totalPosts}</p>
                  <p className="text-xs text-gray-400">Wszystkich</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white/5 border-white/10">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Eye className="w-8 h-8 text-green-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats.publishedPosts}</p>
                  <p className="text-xs text-gray-400">Opublikowanych</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white/5 border-white/10">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Edit2 className="w-8 h-8 text-yellow-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats.draftPosts}</p>
                  <p className="text-xs text-gray-400">Szkiców</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white/5 border-white/10">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <BarChart3 className="w-8 h-8 text-blue-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats.totalViews}</p>
                  <p className="text-xs text-gray-400">Wyświetleń</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-white/5 border-white/10">
        <CardContent className="pt-4">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-gray-400 text-sm">Szukaj</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Szukaj po tytule..."
                  className="pl-10 bg-white/5 border-white/10 text-white"
                />
              </div>
            </div>
            <div className="w-40">
              <Label className="text-gray-400 text-sm">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="bg-white/5 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Wszystkie</SelectItem>
                  <SelectItem value="published">Opublikowane</SelectItem>
                  <SelectItem value="draft">Szkice</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Label className="text-gray-400 text-sm">Kategoria</Label>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="bg-white/5 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Wszystkie</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name?.pl || cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" variant="outline" className="border-white/20">
              <Filter className="w-4 h-4 mr-2" />
              Filtruj
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Posts list */}
      {posts.length === 0 ? (
        <Card className="bg-white/5 border-white/10">
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 mx-auto mb-4 text-gray-500" />
            <p className="text-gray-400">Brak artykułów</p>
            <Button onClick={() => openEditor()} className="mt-4">
              Utwórz pierwszy artykuł
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {posts.map(post => (
            <Card key={post.id} className="mb-4 bg-white/5 border-white/10 hover:border-white/20 transition-colors">
              <CardContent className="py-4">
                <div className="flex items-center gap-4">
                  {/* Thumbnail */}
                  <div className="w-20 h-20 shrink-0 rounded-lg overflow-hidden bg-white/10">
                    {post.featuredImage ? (
                      <img 
                        src={post.featuredImage} 
                        alt="" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="w-8 h-8 text-gray-500" />
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-white truncate">
                        {post.title?.pl || post.title}
                      </h3>
                      {post.isFeatured && (
                        <Star className="w-4 h-4 text-yellow-400 fill-yellow-400 shrink-0" />
                      )}
                    </div>
                    <p className="text-sm text-gray-400 line-clamp-1">
                      {post.excerpt?.pl || 'Brak opisu'}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span className={`px-2 py-0.5 rounded ${
                        post.status === 'published' 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {post.status === 'published' ? 'Opublikowany' : 'Szkic'}
                      </span>
                      {post.category && (
                        <span>{post.category.name?.pl || post.category.name}</span>
                      )}
                      <span className="flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        {post.viewCount || 0}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleFeatured(post.id, post.isFeatured)}
                      title={post.isFeatured ? 'Usuń z polecanych' : 'Dodaj do polecanych'}
                    >
                      <Star className={`w-4 h-4 ${post.isFeatured ? 'text-yellow-400 fill-yellow-400' : 'text-gray-400'}`} />
                    </Button>
                    {post.status === 'published' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(`/pl/blog/${post.slug}`, '_blank')}
                        title="Zobacz na stronie"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditor(post)}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(post.id)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="flex justify-center gap-2 pt-4">
              {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(p => (
                <Button
                  key={p}
                  variant={p === page ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPage(p)}
                  className={p !== page ? 'border-white/20' : ''}
                >
                  {p}
                </Button>
              ))}
            </div>
          )}
        </div>
      )}
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="mt-6 space-y-6">
          <div className="flex justify-between items-center">
            <p className="text-gray-400 text-sm">
              Zarządzaj kategoriami bloga. Nazwy kategorii są wielojęzyczne.
            </p>
            <div className="flex gap-2">
              {categories.length === 0 && (
                <Button onClick={initializeCategories} variant="outline" className="border-primary text-primary">
                  <FolderOpen className="w-4 h-4 mr-2" />
                  Inicjalizuj domyślne
                </Button>
              )}
              <Button 
                onClick={() => { setEditingCategory(emptyCategoryForm); setIsCreatingCategory(true); }} 
                className="bg-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nowa kategoria
              </Button>
            </div>
          </div>

          <div className="grid gap-4">
            {categories.map((cat) => (
              <Card key={cat.id} className="bg-white/5 border-white/10">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                      <FolderOpen className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-white font-medium">
                        {getContentWithFallback(cat.name, 'pl') || cat.name}
                      </h3>
                      <p className="text-gray-500 text-sm">Slug: {cat.slug}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => { setEditingCategory(cat); setIsCreatingCategory(false); }}
                      className="text-gray-400 hover:text-white"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteCategory(cat.id)}
                      className="text-gray-400 hover:text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}

            {categories.length === 0 && (
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-8 text-center">
                  <FolderOpen className="w-12 h-12 mx-auto text-gray-500 mb-4" />
                  <p className="text-gray-400">Brak kategorii bloga</p>
                  <p className="text-gray-500 text-sm mt-2">Użyj przycisku "Inicjalizuj domyślne" aby utworzyć podstawowe kategorie</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Category Edit Modal */}
      {editingCategory && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <Card className="bg-gray-900 border-white/10 w-full max-w-lg">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white">
                {isCreatingCategory ? 'Nowa kategoria' : 'Edytuj kategorię'}
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => { setEditingCategory(null); setIsCreatingCategory(false); }}>
                <X className="w-5 h-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Slug */}
              <div className="space-y-2">
                <Label className="text-gray-300">Slug (identyfikator URL)</Label>
                <Input
                  value={editingCategory.slug || ''}
                  onChange={(e) => setEditingCategory({ ...editingCategory, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                  placeholder="np. aktualnosci, poradniki"
                  className="bg-white/5 border-white/10 text-white"
                  disabled={!isCreatingCategory}
                />
              </div>

              {/* Order */}
              <div className="space-y-2">
                <Label className="text-gray-300">Kolejność sortowania</Label>
                <Input
                  type="number"
                  value={editingCategory.order || 0}
                  onChange={(e) => setEditingCategory({ ...editingCategory, order: parseInt(e.target.value) || 0 })}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>

              {/* Language Tabs for Name */}
              <div className="space-y-4">
                <Label className="text-gray-300">Nazwa kategorii</Label>
                <LanguageTabs
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                  size="small"
                />
                
                <Input
                  value={editingCategory.name?.[activeTab] || ''}
                  onChange={(e) => setEditingCategory({ 
                    ...editingCategory, 
                    name: { ...editingCategory.name, [activeTab]: e.target.value } 
                  })}
                  placeholder={`Nazwa w języku ${activeTab.toUpperCase()}`}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label className="text-gray-300">Opis (opcjonalny)</Label>
                <textarea
                  value={editingCategory.description?.[activeTab] || ''}
                  onChange={(e) => setEditingCategory({ 
                    ...editingCategory, 
                    description: { ...editingCategory.description, [activeTab]: e.target.value } 
                  })}
                  placeholder={`Opis kategorii w języku ${activeTab.toUpperCase()}`}
                  rows={2}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white resize-none"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                <Button variant="outline" onClick={() => { setEditingCategory(null); setIsCreatingCategory(false); }}>
                  Anuluj
                </Button>
                <Button onClick={handleSaveCategory} disabled={saving} className="bg-primary">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Zapisz
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminBlog;
